import { Component, EventEmitter, Input, Output, forwardRef, OnDestroy } from '@angular/core'
import { TranslateModule } from '@ngx-translate/core'
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { NgClass, NgIf } from '@angular/common'

@Component({
    selector: 'app-search-input',
    standalone: true,
    imports: [
        TranslateModule,
        FormsModule,
        NgIf,
        NgClass
    ],
    templateUrl: './search-input.component.html',
    styleUrls: ['./search-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchInputComponent),
            multi: true
        }
    ]
})
export class SearchInputComponent implements ControlValueAccessor, OnDestroy {
    @Input() value: string = ''
    @Output() valueChange: EventEmitter<string> = new EventEmitter<string>()

    @Input() debounceMs: number = 300

    @Input() disabled: boolean = false
    @Input() searchDisabled: boolean = false
    @Input() placeholder: string = ''

    private debounceTimer: ReturnType<typeof setTimeout> | null = null


    ngOnDestroy(): void {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }
    }

    // ControlValueAccessor hooks
    private onChange: (value: string) => void = () => {
    }
    private onTouched: () => void = () => {
    }

    writeValue(value: string | null): void {
        this.value = value ?? ''
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled
    }

    // Internal change handler to sync both outputs
    private emitValue(value: string): void {
        this.onChange(value)
        this.valueChange.emit(value)
    }

    onInternalChange(value: string): void {
        this.value = value

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }

        this.debounceTimer = setTimeout(() => {
            this.debounceTimer = null
            this.emitValue(this.value)
        }, this.debounceMs)
    }


    flushPendingChange(): void {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
            this.debounceTimer = null
        }
        this.emitValue(this.value)
    }

    onEnter(event: Event): void {
        event.preventDefault()
        this.flushPendingChange()
    }


    onBlur(): void {
        this.flushPendingChange()
        this.onTouched()
    }
}
